import {
  BasketCustomerOfferEntity,
  ResponsesBasketCustomerOrderFilterResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import { OperationType } from '../../../pages/BatchTicket/components/DynamicAsset/contexts/constants';
import { messageError } from '../constants';

import { normalizeCustomerBasketOffersCoe } from './coes';
import { normalizeCustomerBasketOffersTreasury } from './directTreasury';
import { normalizeCustomerBasketOffersEquity } from './equity';
import { normalizeCustomerBasketOffersFixedIncome } from './fixedIncome';
import { normalizeCustomerBasketOffersFund } from './funds';

export const normalizeCustomerBasketOffersData = (data?: BasketCustomerOfferEntity[] | null) => {
  return (
    data?.map((item) => ({
      ...item,
      operationType: (item.side === 'C' ? 'buy' : 'sell') as OperationType,
      amountFormatted: Utils.Mask.Money(item.amount),
      requestedAmountFormatted: Utils.Mask.Money(item.requestedAmount),
      requestedPriceFormatted: Utils.Mask.Money(item.requestedPrice),
      offer: {
        ...item.offer,
        maturityDateFormatted: item.offer.maturityDate
          ? Utils.Mask.DateFormat(item.offer.maturityDate, 'dd/MM/yyyy')
          : messageError,
        minimumApplicationFormatted: Utils.Mask.Money(item.offer.minimumApplication),
        bidPriceFormatted: Utils.Mask.Money(item.offer.bidPrice),
        askPriceFormatted: Utils.Mask.Money(item.offer.askPrice),
        lastPriceFormatted: Utils.Mask.Money(item.offer.lastPrice),
        openPriceFormatted: Utils.Mask.Money(item.offer.openPrice),
        participateMinimumAmountFormatted: Utils.Mask.Money(item.offer.participateMinimumAmount),
        applicationMinimumAmountFormatted: Utils.Mask.Money(item.offer.applicationMinimumAmount),
        lastUpdatedAtFormatted: item.offer.lastUpdatedAt
          ? Utils.Mask.DateFormat(item.offer.lastUpdatedAt, 'HH:mm')
          : messageError,
        applicationLimitDateFormatted: item.offer.applicationLimitDate
          ? Utils.Mask.DateFormat(item.offer.applicationLimitDate, 'dd/MM/yyyy')
          : messageError,
      },
    })) || []
  );
};

export const baseNormalization = (item: any) => ({
  id: item.basketCustomerOfferId,
  acceptance: item.basketOfferStatus.statusId === 2005,
  basketOfferFiles: item.basketOfferFiles?.reverse() || [],
  basketOfferStatus: {
    statusId: item.basketOfferStatus.statusId ?? undefined,
    statusDescription: item.basketOfferStatus.status ?? undefined,
  },
  orderType: item.orderType ?? undefined,
  marketType: item.marketType ?? undefined,
  billingTypeId: item.billingType?.billingTypeId ?? undefined,
  amount: item.amount ?? undefined,
  requestedAmount: item.requestedAmount ?? undefined,
  quantity: item.quantity ?? undefined,
  requestedPrice: item.requestedPrice ?? undefined,
  requestedQuantity: item.requestedQuantity ?? undefined,
  operationType: item.operationType as OperationType,
});

const getOperation = (data: ResponsesBasketCustomerOrderFilterResponse) => {
  const operation = data?.basketCustomerOrder?.customerOffers?.[0]?.basketOffers?.[0]?.side;
  if (!operation) {
    return 'RESGATE';
  }
  return operation === 'C' ? 'INVESTIMENTO' : 'RESGATE';
};

const getOperationType = (data: ResponsesBasketCustomerOrderFilterResponse): OperationType => {
  const operation = data?.basketCustomerOrder?.customerOffers?.[0]?.basketOffers?.[0]?.side;
  if (!operation) {
    return 'sell';
  }
  return operation === 'C' ? 'buy' : 'sell';
};

const getAdvisorName = (data: ResponsesBasketCustomerOrderFilterResponse) => {
  const firstName = data?.basketCustomerOrder?.user?.resourceFirstName || '';
  const lastName = data?.basketCustomerOrder?.user?.resourceLastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || messageError;
};

export const normalizeGeneralData = ({
  offers,
  data,
}: {
  offers: ReturnType<
    | typeof normalizeCustomerBasketOffersFixedIncome
    | typeof normalizeCustomerBasketOffersFund
    | typeof normalizeCustomerBasketOffersCoe
    | typeof normalizeCustomerBasketOffersEquity
    | typeof normalizeCustomerBasketOffersTreasury
  >;
  data: ResponsesBasketCustomerOrderFilterResponse;
}) => ({
  data: {
    offers,
    basketAdvisorOrderHash: data?.basketCustomerOrder?.basketAdvisorOrderHash || messageError,
    totalCustomerOffersAmount: Utils.Mask.Money(
      data?.basketCustomerOrder?.totalCustomerOffersAmount || 0
    ),
    totalCustomerOffers: data?.basketCustomerOrder?.totalCustomerOffers || 0,
    customerName: data?.basketCustomerOrder?.customer.customerName || messageError,
    advisorName: getAdvisorName(data),
    createDate: Utils.Mask.DateFormat(
      data?.basketCustomerOrder?.createDate || new Date(),
      'dd MMM yyyy'
    ),
    operation: getOperation(data),
    operationType: getOperationType(data),
  },
});

export * from './coes';
export * from './directTreasury';
export * from './equity';
export * from './fixedIncome';
export * from './funds';
